import React, { useState, useEffect, useCallback } from 'react';
import { Loader2, FileText, AlertTriangle, Info, UploadCloud, CheckCircle, XCircle, ChevronRight, FileQuestion, RefreshCw } from 'lucide-react';
import { toast } from 'react-hot-toast';
import Papa from 'papaparse';
import { supabase } from '../../lib/supabase';
import { useImportSetup } from '../../hooks/useImportSetup';
import { useCsvParser } from '../../hooks/useCsvParser';
import { TableSelector } from './import/TableSelector'; 
// Import PreviewStatus enum
import { ProcessedSchema, DbSchemaColumn, CsvRowData, PreviewRow, MappedColumnInfo, PreviewStatus } from '../../types/import';
// Import the new PreviewTable component
import { PreviewTable } from './import/PreviewTable';

// Interface remains the same for now
interface ImportContentProps {
  // Define any props if needed, e.g., initial configurations
}

// The main component
const ImportContent: React.FC<ImportContentProps> = () => {
  // --- Hooks --- 
  const {
    // Remove dbSchemaJson, schema is handled internally by the hook
    tableNames,
    selectedTable,
    selectedTableColumns,
    identifierColumn, // Add identifierColumn
    isLoadingSchema,
    schemaError,
    handleTableChange,
    handleIdentifierChange // Add handleIdentifierChange
  } = useImportSetup();

  const {
    file,
    csvHeaders,
    csvData,
    isParsing,
    parseError,
    handleFileChange: handleCsvFileChange, // Renamed to avoid conflict
    resetCsvData
  } = useCsvParser();

  // --- State for Mapping, Preview and Import --- 
  const [columnMapping, setColumnMapping] = useState<{ [key: string]: string }>({});
  const [mappedColumnsInfo, setMappedColumnsInfo] = useState<MappedColumnInfo[]>([]); // For mapping confirmation
  const [previewData, setPreviewData] = useState<PreviewRow[]>([]);
  const [isPreviewLoading, setIsPreviewLoading] = useState(false);
  const [previewError, setPreviewError] = useState<string | null>(null);
  const [isImporting, setIsImporting] = useState(false);
  const [importError, setImportError] = useState<string | null>(null);
  const [importSuccessCount, setImportSuccessCount] = useState(0);
  const [importSkippedCount, setImportSkippedCount] = useState(0);
  const [importErrorCount, setImportErrorCount] = useState(0);

  // --- Effects --- 

  // Reset states when table changes
  useEffect(() => {
    resetCsvData();
    setColumnMapping({});
    setMappedColumnsInfo([]);
    setPreviewData([]);
    setIsPreviewLoading(false);
    setPreviewError(null);
    setIsImporting(false);
    setImportError(null);
    setImportSuccessCount(0);
    setImportSkippedCount(0);
    setImportErrorCount(0);
    // Identifier column is reset within useImportSetup
  }, [selectedTable, resetCsvData]);

  // Update mapping confirmation when mapping or headers change
  useEffect(() => {
    const info: MappedColumnInfo[] = csvHeaders.map((header: string) => ({
      csvColumn: header,
      dbColumn: columnMapping[header] || null,
      isIdentifier: columnMapping[header] === identifierColumn && identifierColumn !== '',
    }));
    setMappedColumnsInfo(info);
  }, [columnMapping, csvHeaders, identifierColumn]);

  // --- Handlers --- 

  const handleMappingChange = (csvHeader: string, dbColumn: string) => {
    setColumnMapping(prev => ({ ...prev, [csvHeader]: dbColumn }));
  };

  // Placeholder - will be complex
  const handlePreview = useCallback(async () => {
    if (!file || !selectedTable || !identifierColumn || Object.keys(columnMapping).length === 0 || csvHeaders.length === 0) {
      toast.error('Por favor, selecione a tabela, o arquivo, a coluna identificadora e mapeie as colunas.');
      return;
    }
    
    setIsPreviewLoading(true);
    setPreviewError(null);
    setPreviewData([]);

    // --- Phase 1: Parse CSV and collect identifiers --- 
    const collectedData: { identifier: string | number | null; rowData: CsvRowData }[] = [];
    // Find the CSV header that maps to the DB identifier column
    const csvIdentifierHeader = Object.keys(columnMapping).find(csvCol => columnMapping[csvCol] === identifierColumn);

    if (!csvIdentifierHeader) {
        toast.error(`A coluna CSV correspondente ao identificador '${identifierColumn}' não foi mapeada.`);
        setIsPreviewLoading(false);
        return;
    }

    console.log(`Starting CSV parse. Identifier column: DB='${identifierColumn}', CSV='${csvIdentifierHeader}'`);

    const parsePromise = new Promise<void>((resolve, reject) => {
      let rowCount = 0;
      Papa.parse<CsvRowData>(file, {
        header: true,
        skipEmptyLines: true,
        step: (results, parser) => {
          rowCount++;
          const rowData = results.data;
          const identifierValue = rowData[csvIdentifierHeader];

          // Basic validation for identifier
          if (identifierValue === null || identifierValue === undefined || identifierValue === '') {
            console.warn(`Row ${rowCount}: Skipped row due to empty identifier value in column '${csvIdentifierHeader}'.`);
            // Optionally add to previewData with error/skipped status later
            return; // Skip this row for now
          }
          
          // Convert boolean identifier to string if necessary
          const finalIdentifier: string | number | null = typeof identifierValue === 'boolean' ? String(identifierValue) : identifierValue;
          
          collectedData.push({ identifier: finalIdentifier, rowData });
        },
        complete: (results) => {
          console.log(`CSV Parsing Complete. Rows processed: ${rowCount}, Identifiers collected: ${collectedData.length}`);
          if (results.errors.length > 0) {
            console.error("CSV Parse errors:", results.errors);
            reject(new Error(`Erros encontrados ao ler o CSV: ${results.errors.map(e => e.message).join('; ')}`));
          } else {
             resolve(); // Parsing successful
          }
        },
        error: (error: Error) => {
          console.error("CSV Parse fatal error:", error);
          reject(new Error(`Erro fatal ao ler o CSV: ${error.message}`));
        }
      });
    });

    toast.promise(
        parsePromise.then(async () => {
            // --- Phase 2: Fetch DB data and compare --- 
            console.log("Phase 2: Fetching DB data...");
            setIsPreviewLoading(true); // Keep loading state during DB fetch

            if (collectedData.length === 0) {
                console.log("No valid identifiers collected from CSV. Nothing to preview.");
                setPreviewData([]);
                setIsPreviewLoading(false);
                return "Nenhum dado válido com identificador encontrado no CSV.";
            }

            const identifiers = collectedData.map(d => d.identifier).filter(id => id !== null) as (string | number)[];
            const dbDataMap = new Map<string | number, any>();

            try {
                // Fetch existing records from Supabase in batches (e.g., 500 at a time)
                const BATCH_SIZE = 500;
                for (let i = 0; i < identifiers.length; i += BATCH_SIZE) {
                    const batchIdentifiers = identifiers.slice(i, i + BATCH_SIZE);
                    console.log(`Fetching batch ${Math.floor(i / BATCH_SIZE) + 1}: ${batchIdentifiers.length} identifiers.`);
                    
                    // Select only the identifier column and the columns mapped in the CSV
                    const dbColumnsToSelect = [identifierColumn, ...new Set(Object.values(columnMapping).filter(dbCol => dbCol && dbCol !== identifierColumn))];
                    
                    const { data: dbRecords, error: dbError } = await supabase
                        .from(selectedTable)
                        .select(dbColumnsToSelect.join(',')) // Select necessary columns
                        .in(identifierColumn, batchIdentifiers);

                    if (dbError) {
                        console.error("Supabase fetch error:", dbError);
                        throw new Error(`Erro ao buscar dados do DB: ${dbError.message}`);
                    }
                    
                    // Populate the map for quick lookup
                    dbRecords?.forEach(record => {
                        if (record && record[identifierColumn] !== null && record[identifierColumn] !== undefined) {
                             dbDataMap.set(record[identifierColumn], record);
                        }
                    });
                }
                console.log(`DB Fetch complete. Found ${dbDataMap.size} existing records.`);

                // --- Comparison Logic --- 
                const finalPreviewData: PreviewRow[] = [];
                for (const { identifier, rowData } of collectedData) {
                    if (identifier === null) continue; // Should have been filtered, but double-check

                    // Type existingDbRecord as a generic Record
                    const existingDbRecord: Record<string, any> | undefined = dbDataMap.get(identifier);
                    const csvRowForDb: { [key: string]: any } = {};
                    const changes: { [dbColumn: string]: { oldValue: any; newValue: any } } = {};
                    let hasChanges = false;

                    // Prepare CSV data based on mapping
                    for (const csvCol in columnMapping) {
                        const dbCol = columnMapping[csvCol];
                        if (dbCol && (rowData as any).hasOwnProperty(csvCol)) { // Use any temporarily
                            csvRowForDb[dbCol] = (rowData as any)[csvCol];
                        }
                    }

                    if (!existingDbRecord) {
                        // Record is New - Check required columns
                        let missingRequired = false;
                        let errorMsg = 'Nova linha inválida:';
                        for (const colDef of selectedTableColumns) {
                             // Check if column is required (NOT NULL and no DEFAULT) and not the identifier itself
                            if (colDef.is_nullable === 'NO' && !colDef.column_default && colDef.column_name !== identifierColumn) {
                                // Check if this required column is missing in the prepared CSV data
                                if (!csvRowForDb.hasOwnProperty(colDef.column_name) || csvRowForDb[colDef.column_name] === null || csvRowForDb[colDef.column_name] === '') {
                                    missingRequired = true;
                                    errorMsg += ` Valor ausente/inválido para coluna obrigatória '${colDef.column_name}'.`;
                                }
                            }
                        }

                        finalPreviewData.push({
                            id: identifier,
                            // Set status to Error if required columns are missing
                            status: missingRequired ? PreviewStatus.Error : PreviewStatus.New,
                            csvData: csvRowForDb, 
                            dbData: null,
                            error: missingRequired ? errorMsg : undefined, 
                        });
                    } else {
                        // Record exists, compare values
                        for (const dbCol in csvRowForDb) {
                            if (dbCol === identifierColumn) continue; // Don't compare identifier itself
                            
                            const csvValueRaw = csvRowForDb[dbCol];
                            const dbValueRaw = existingDbRecord ? existingDbRecord[dbCol] : undefined;

                            // Find column definition to check data type
                            const colDef = selectedTableColumns.find(c => c.column_name === dbCol);
                            const dbDataType = colDef?.data_type.toLowerCase() || 'text';

                            let areEqual = false;
                            let csvValueParsed: any = csvValueRaw;
                            let dbValueParsed: any = dbValueRaw;

                            try {
                                // Type-aware comparison
                                if (dbDataType.includes('numeric') || dbDataType.includes('int') || dbDataType.includes('double') || dbDataType.includes('real')) {
                                    // Attempt to compare as numbers
                                    csvValueParsed = csvValueRaw === null || csvValueRaw === undefined || csvValueRaw === '' ? null : Number(csvValueRaw);
                                    dbValueParsed = dbValueRaw === null || dbValueRaw === undefined ? null : Number(dbValueRaw);
                                    // Handle NaN cases - if either is NaN they are only equal if both input were invalid numbers
                                    if (isNaN(csvValueParsed) && isNaN(dbValueParsed)) areEqual = true; 
                                    else if (isNaN(csvValueParsed) || isNaN(dbValueParsed)) areEqual = false;
                                    else areEqual = csvValueParsed === dbValueParsed;
                                } else if (dbDataType.includes('bool')) {
                                    // Attempt to compare as booleans (handle common string representations)
                                    const trueValues = ['true', 't', '1', 'yes', 'y', 'on'];
                                    const falseValues = ['false', 'f', '0', 'no', 'n', 'off', '']; // Treat empty string as false?
                                    csvValueParsed = csvValueRaw === null || csvValueRaw === undefined ? null : trueValues.includes(String(csvValueRaw).toLowerCase());
                                    dbValueParsed = dbValueRaw; // Assume DB boolean is already correct type
                                    areEqual = csvValueParsed === dbValueParsed;
                                } else if (dbDataType.includes('uuid')) {
                                    // Compare UUIDs as strings, case-insensitively?
                                    csvValueParsed = String(csvValueRaw ?? '').toLowerCase();
                                    dbValueParsed = String(dbValueRaw ?? '').toLowerCase();
                                     areEqual = csvValueParsed === dbValueParsed;
                                } else if (dbDataType.includes('date') || dbDataType.includes('timestamp')) {
                                    // Basic date comparison (can be improved significantly)
                                    // This might fail for different formats. Consider using a library like date-fns or moment if needed.
                                    try {
                                        const dateCsv = csvValueRaw ? new Date(csvValueRaw).toISOString() : null;
                                        const dateDb = dbValueRaw ? new Date(dbValueRaw).toISOString() : null;
                                        csvValueParsed = dateCsv;
                                        dbValueParsed = dateDb;
                                        areEqual = dateCsv === dateDb;
                                    } catch (dateError) {
                                        console.warn(`Could not compare dates for column ${dbCol}:`, dateError);
                                        areEqual = String(csvValueRaw ?? '') === String(dbValueRaw ?? ''); // Fallback to string compare
                                    }
                                } else {
                                    // Default to string comparison
                                     csvValueParsed = String(csvValueRaw ?? '');
                                     dbValueParsed = String(dbValueRaw ?? '');
                                    areEqual = csvValueParsed === dbValueParsed;
                                }
                            } catch (compareError) {
                                 console.warn(`Error comparing column ${dbCol}:`, compareError);
                                 // Fallback to string comparison on error
                                 areEqual = String(csvValueRaw ?? '') === String(dbValueRaw ?? '');
                            }

                            if (!areEqual) {
                                changes[dbCol] = { oldValue: dbValueRaw, newValue: csvValueRaw }; // Store original values in changes
                                hasChanges = true;
                            }
                        }

                        finalPreviewData.push({
                            id: identifier,
                            status: hasChanges ? PreviewStatus.Update : PreviewStatus.Identical,
                            csvData: csvRowForDb,
                            dbData: existingDbRecord,
                            changes: hasChanges ? changes : undefined,
                        });
                    }
                }
                
                // Detailed Log before setting state
                console.log("Detailed Preview Data (First 5 Rows):");
                console.log(JSON.stringify(finalPreviewData.slice(0, 5), (key, value) => {
                    // Avoid logging huge data blobs if dbData/csvData are large, show keys only
                    if ((key === 'dbData' || key === 'csvData') && value && typeof value === 'object') {
                        return Object.keys(value);
                    }
                    return value;
                 }, 2));

                setPreviewData(finalPreviewData);
                setIsPreviewLoading(false);
                console.log("Preview generation complete."); // Simplified log here
                return `Pré-visualização gerada: ${finalPreviewData.length} linhas analisadas.`;

            } catch (error: any) {
                console.error("Error during Phase 2 (DB Fetch/Compare):", error);
                setPreviewError(error.message || 'Erro desconhecido na Fase 2');
                setIsPreviewLoading(false);
                throw error; // Re-throw for toast
            }

        }),
        {
            loading: 'Fase 1: Lendo arquivo CSV...',
            success: (message) => message,
            error: (err) => `Falha na pré-visualização: ${err.message || 'Erro desconhecido'}`,
        }
    );

  }, [file, selectedTable, identifierColumn, columnMapping, csvHeaders]);

  // Placeholder - will be complex
  const handleImport = useCallback(async () => {
      if (previewData.length === 0) {
          toast.error("Gere a pré-visualização antes de importar.");
          return;
      }
      if (!selectedTable || !identifierColumn) {
          toast.error("Tabela ou coluna identificadora não selecionada.");
          return;
      }
  
      setIsImporting(true);
      setImportError(null);
      setImportSuccessCount(0);
      setImportSkippedCount(0);
      setImportErrorCount(0);
  
      console.log("Starting import process...");

      const rowsToProcess = previewData
          .filter(row => [PreviewStatus.New, PreviewStatus.Update].includes(row.status));
        
      const dataToImport: any[] = []; // Array to hold the final objects for upsert

      for (const row of rowsToProcess) {
          let rowDataForUpsert = { ...row.csvData }; // Start with data from CSV (mapped columns)

          if (row.status === PreviewStatus.Update && row.dbData) {
              // For updates, ensure required columns not in CSV data are included from DB data
               for (const colDef of selectedTableColumns) {
                   // Check if column is required (NOT NULL and no DEFAULT)
                  if (colDef.is_nullable === 'NO' && !colDef.column_default) {
                      // Check if this required column is NOT present in the CSV data (wasn't mapped/provided)
                      if (!rowDataForUpsert.hasOwnProperty(colDef.column_name)) {
                          // Copy the existing value from dbData
                           if (row.dbData.hasOwnProperty(colDef.column_name)) {
                               rowDataForUpsert[colDef.column_name] = row.dbData[colDef.column_name];
                           } else {
                              // This case is unlikely if dbData came from the DB, but handle defensively
                              console.warn(`Required column ${colDef.column_name} missing in both CSV and DB data for row ID ${row.id}. Setting to null.`);
                              rowDataForUpsert[colDef.column_name] = null; // Explicitly set null, might still fail if NOT NULL
                           }
                      }
                  }
              }
          }
          // Note: NEW rows that passed preview validation should already have required mapped columns.
          // If a NEW row is missing an unmapped required column, it should have been marked as Error in preview.

          dataToImport.push(rowDataForUpsert);
      }

      if (dataToImport.length === 0) {
          toast.error("Nenhuma linha válida (Nova ou Atualizada) para importar após verificação.");
          setIsImporting(false);
          return;
      }

      // Use a slightly smaller batch size for writes compared to reads
      const BATCH_SIZE = 200; 
      let currentSuccess = 0;
      let currentErrors = 0;
      let firstErrorMessage: string | null = null;

      const importPromise = async () => {
        for (let i = 0; i < dataToImport.length; i += BATCH_SIZE) {
          const batch = dataToImport.slice(i, i + BATCH_SIZE);
          console.log(`Importing batch ${Math.floor(i / BATCH_SIZE) + 1}: ${batch.length} records.`);
          
          try {
            // Perform the upsert operation
            const { error: upsertError, count } = await supabase
              .from(selectedTable)
              .upsert(batch, { 
                  onConflict: identifierColumn, // Use the selected identifier column for conflict resolution
                  // defaultToNull: false // Consider setting this if empty strings should overwrite nulls explicitly
               });

            if (upsertError) {
               console.error(`Batch ${Math.floor(i / BATCH_SIZE) + 1} Upsert Error:`, upsertError);
              // Increment error count for the entire batch on error
               currentErrors += batch.length;
               if (!firstErrorMessage) {
                   firstErrorMessage = upsertError.message;
               }
              // Optionally break or continue on error?
              // For now, we'll continue with the next batch but log the error
              toast.error(`Erro no lote ${Math.floor(i / BATCH_SIZE) + 1}: ${upsertError.message}`, { duration: 5000 });
              
            } else {
              // Supabase count might not be reliable for upsert details across rows.
              // Assume success for the batch if no error is thrown.
              currentSuccess += batch.length; 
               console.log(`Batch ${Math.floor(i / BATCH_SIZE) + 1} processed.`);
            }
          } catch (batchError: any) {
            console.error(`Critical error processing batch ${Math.floor(i / BATCH_SIZE) + 1}:`, batchError);
             currentErrors += batch.length; // Count the whole batch as error on critical failure
             if (!firstErrorMessage) {
               firstErrorMessage = batchError.message || 'Erro desconhecido no lote';
             }
            toast.error(`Erro crítico no lote ${Math.floor(i / BATCH_SIZE) + 1}: ${batchError.message}`, { duration: 5000 });
             // Stop processing further batches on critical error
            // throw batchError; // Re-throw to stop the promise chain
            break; // Stop processing further batches
          }
        }
      };


      toast.promise(
          importPromise().finally(() => {
              // Update final counts after all batches are processed or an error occurred
              setImportSuccessCount(currentSuccess);
              setImportErrorCount(currentErrors);
              setImportSkippedCount(0); // Reset skipped count
              setImportError(firstErrorMessage);
              setIsImporting(false);
               console.log(`Import finished. Success: ${currentSuccess}, Errors: ${currentErrors}`);
               // Provide a summary toast
               if (currentErrors > 0 && currentSuccess > 0) {
                   toast.error(`Importação concluída com ${currentSuccess} sucesso(s) e ${currentErrors} erro(s).`, { duration: 6000 });
               } else if (currentErrors > 0) {
                   toast.error(`Importação falhou com ${currentErrors} erro(s). Verifique os logs.`, { duration: 6000 });
               } else {
                   toast.success(`Importação concluída com sucesso! ${currentSuccess} registros processados.`, { duration: 6000 });
               }
           }),
          {
              loading: `Importando ${dataToImport.length} registros...`,
              // Success/Error messages are handled in the finally block above
              success: () => "Processamento de importação finalizado.",
              error: (err) => `Falha crítica na importação: ${err.message || 'Erro desconhecido'}`, 
          }
      );
  }, [previewData, selectedTable, identifierColumn, supabase]);


  // --- UI Rendering --- 
  return (
    <div className="container mx-auto p-6 bg-gray-50 min-h-screen">
      <h1 className="text-2xl font-bold mb-6 text-gray-800">Importar Dados</h1>

      {/* Step 1: Table Selection - Uses the new component */}
      <TableSelector 
        tableNames={tableNames}
        selectedTable={selectedTable}
        handleTableChange={handleTableChange} // Pass the handler directly (expects event)
        isLoadingSchema={isLoadingSchema}
        schemaError={schemaError}
        selectedTableColumns={selectedTableColumns}
      />

      {/* Step 2: File Upload - Basic structure, needs a dedicated component later */} 
      {selectedTable && (
        <div className="mb-6 p-4 border rounded-md shadow-sm bg-white">
          <h2 className="text-lg font-semibold mb-3 text-gray-700">2. Upload do Arquivo CSV</h2>
          <input
            type="file"
            accept=".csv"
            onChange={handleCsvFileChange}
            disabled={isParsing}
            className="block w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-full file:border-0 file:text-sm file:font-semibold file:bg-indigo-50 file:text-indigo-700 hover:file:bg-indigo-100 disabled:opacity-50"
          />
          {isParsing && <p className="text-sm text-blue-600 mt-2">Processando arquivo...</p>}
          {parseError && <p className="text-sm text-red-600 mt-2">Erro no arquivo: {parseError}</p>}
          {file && !isParsing && !parseError && (
            <p className="text-sm text-green-600 mt-2">Arquivo "{file.name}" carregado. Cabeçalhos detectados: {csvHeaders.length}</p>
          )}
        </div>
      )}

      {/* Step 3: Column Mapping - Basic structure, needs a dedicated component later */} 
      {selectedTable && file && csvHeaders.length > 0 && selectedTableColumns.length > 0 && !isParsing && (
        <div className="mb-6 p-4 border rounded-md shadow-sm bg-white">
          <h2 className="text-lg font-semibold mb-3 text-gray-700">3. Mapeamento de Colunas</h2>
          
          {/* Identifier Column Selection */} 
          <div className="mb-4">
            <label htmlFor="identifierColumn" className="block text-sm font-medium text-gray-600 mb-1">
              Coluna Identificadora Única (do Banco de Dados):
            </label>
            <select
              id="identifierColumn"
              value={identifierColumn}
              onChange={handleIdentifierChange}
              className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
            >
              <option value="" disabled>-- Selecione a coluna chave --</option>
              {/* Use correct properties from DbSchemaColumn and add explicit type for col */}
              {selectedTableColumns.filter(col => col.isPrimaryKey || col.isUnique).map((col: DbSchemaColumn) => (
                <option key={col.column_name} value={col.column_name}>
                  {col.column_name} ({col.isPrimaryKey ? 'PK' : 'Única'})
                </option>
              ))}
            </select>
            {!identifierColumn && <p className="text-xs text-yellow-600 mt-1">Selecione a coluna que identifica unicamente cada registro na tabela.</p>}
          </div>

          {/* Column Mapping Table */} 
          <p className="text-sm text-gray-600 mb-2">Mapeie as colunas do seu arquivo CSV para as colunas da tabela '{selectedTable}':</p>
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Coluna CSV</th>
                  <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Coluna Tabela ({selectedTable})</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {/* Add explicit type for header */}
                {csvHeaders.map((header: string) => (
                  <tr key={header}>
                    <td className="px-4 py-2 whitespace-nowrap text-sm font-medium text-gray-900">{header}</td>
                    <td className="px-4 py-2 whitespace-nowrap text-sm text-gray-500">
                      <select
                        value={columnMapping[header] || ''}
                        onChange={(e) => handleMappingChange(header, e.target.value)}
                        className="block w-full px-3 py-1.5 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                      >
                        <option value="">-- Ignorar --</option>
                        {/* Use correct properties from DbSchemaColumn and add explicit type for col */}
                        {selectedTableColumns.map((col: DbSchemaColumn) => (
                          <option key={col.column_name} value={col.column_name} disabled={Object.values(columnMapping).includes(col.column_name) && columnMapping[header] !== col.column_name}>
                            {col.column_name} ({col.data_type}){col.isPrimaryKey ? ' PK' : ''}{col.isUnique ? ' Unique' : ''}{col.is_nullable === 'NO' ? ' Not Null' : ''}
                          </option>
                        ))}
                      </select>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          
          {/* Mapping Confirmation - Moved logic here, needs styling */} 
          <div className="mt-4 p-3 bg-indigo-50 border border-indigo-200 rounded">
             <h3 className="text-md font-semibold text-indigo-800 mb-2">Confirmação do Mapeamento:</h3>
             <ul className="list-disc list-inside text-sm space-y-1">
                 {mappedColumnsInfo.filter(info => info.dbColumn).map((info: MappedColumnInfo) => (
                    <li key={info.csvColumn} className={info.isIdentifier ? 'font-bold text-indigo-700' : 'text-gray-700'}>
                         Coluna CSV "{info.csvColumn}" será importada para a coluna "{info.dbColumn}" da tabela.
                         {info.isIdentifier && " (Identificador)"}
                    </li>
                 ))}
                 {mappedColumnsInfo.filter(info => !info.dbColumn).length > 0 && (
                    <li className="text-gray-500 italic">
                       {mappedColumnsInfo.filter(info => !info.dbColumn).length} coluna(s) CSV serão ignoradas.
                    </li>
                 )}
             </ul>
             {mappedColumnsInfo.length > 0 && !mappedColumnsInfo.some(info => info.dbColumn) && (
                 <p className="text-xs text-yellow-600 mt-1">Nenhuma coluna mapeada para importação.</p>
             )}
          </div>

        </div>
      )}

      {/* Step 4: Preview - Basic structure, needs a dedicated component later */} 
      {selectedTable && file && csvHeaders.length > 0 && identifierColumn && Object.values(columnMapping).some(v => v) && (
        <div className="mb-6 p-4 border rounded-md shadow-sm bg-white">
          <div className="flex justify-between items-center mb-3">
             <h2 className="text-lg font-semibold text-gray-700">4. Pré-visualização e Importação</h2>
             <button
                 onClick={handlePreview}
                 disabled={isPreviewLoading || isImporting}
                 className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center space-x-1"
             >
                 {isPreviewLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : <FileQuestion className="h-4 w-4" />}
                 <span>Gerar Pré-visualização</span>
             </button>
          </div>

          {isPreviewLoading && (
            <div className="flex items-center space-x-2 text-gray-500 my-4">
              <Loader2 className="h-5 w-5 animate-spin" />
              <span>Analisando dados e buscando registros existentes...</span>
            </div>
          )}
          {previewError && <p className="text-red-600 bg-red-100 border border-red-400 p-2 rounded my-4">Erro na pré-visualização: {previewError}</p>}

          {/* Preview Table */} 
          {previewData.length > 0 && !isPreviewLoading && (
            <div className="mt-4">
              {/* Render the PreviewTable component, passing only the first 5 rows */}
              <PreviewTable 
                previewData={previewData.slice(0, 5)} // Limit to 5 rows
                columns={selectedTableColumns} 
                identifierColumn={identifierColumn}
              />
              {previewData.length > 5 && (
                  <p className="text-xs text-gray-500 italic mt-2">Exibindo as primeiras 5 linhas de {previewData.length} totais da pré-visualização.</p>
              )}
              {/* Import button activation */} 
              <div className="mt-4 flex justify-end items-center gap-4">
                {/* Summary of changes */}
                <div className="text-sm text-gray-700">
                  {previewData.filter(p => p.status === PreviewStatus.New).length > 0 && (
                    <span className="mr-3">
                       <CheckCircle className="h-4 w-4 inline-block mr-1 text-green-500" />
                       {previewData.filter(p => p.status === PreviewStatus.New).length} Novo(s)
                    </span>
                  )}
                   {previewData.filter(p => p.status === PreviewStatus.Update).length > 0 && (
                     <span className="mr-3">
                       <RefreshCw className="h-4 w-4 inline-block mr-1 text-blue-500" />
                       {previewData.filter(p => p.status === PreviewStatus.Update).length} Atualizar
                     </span>
                   )}
                    {previewData.filter(p => p.status === PreviewStatus.Identical).length > 0 && (
                     <span className="text-gray-500">
                       <Info className="h-4 w-4 inline-block mr-1 text-gray-500" />
                       {previewData.filter(p => p.status === PreviewStatus.Identical).length} Idêntico(s)
                     </span>
                   )}
                   {/* Optionally show Error/Skipped counts too */} 
                </div>
                <button
                  onClick={handleImport}
                  disabled={isImporting || isPreviewLoading || previewData.filter(p => [PreviewStatus.New, PreviewStatus.Update].includes(p.status)).length === 0 }
                  className="px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center space-x-1"
               >
                  {isImporting ? <Loader2 className="h-4 w-4 animate-spin" /> : <UploadCloud className="h-4 w-4"/>}
                  <span>Importar {previewData.filter(p => [PreviewStatus.New, PreviewStatus.Update].includes(p.status)).length} Registros</span>
               </button>
              </div>
              {isImporting && <p className="text-sm text-blue-600 mt-2 text-right">Importando...</p>}
              {importError && <p className="text-sm text-red-600 mt-2 text-right">Erro na importação: {importError}</p>}
              {importSuccessCount + importSkippedCount + importErrorCount > 0 && !isImporting && (
                    <p className="text-sm text-gray-700 mt-2 text-right">
                        Importação concluída: {importSuccessCount} sucesso(s), {importSkippedCount} ignorado(s), {importErrorCount} erro(s).
                    </p>
                )}
            </div>
          )}
           {previewData.length === 0 && !isPreviewLoading && !previewError && file && (
               <p className="text-sm text-gray-500 italic mt-4">Gere a pré-visualização para ver as mudanças propostas.</p>
           )}
        </div>
      )}

    </div>
  );
};

export default ImportContent;